import {
  CurrencyDollarIcon,
  ClockIcon,
  BuildingOffice2Icon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import { ToneChip } from './ToneChip';
import type { ClientTone, RiskSignal, RiskSignalType, GateUrgency } from '@revont/shared-types';
import { ToneIndicator } from './ToneIndicator';

// Icon mapping for risk signal types
const RISK_SIGNAL_ICONS: Record<RiskSignalType, React.ComponentType<{ className?: string }>> = {
  budget_uncertainty: CurrencyDollarIcon,
  timeline_stall: ClockIcon,
  competitor_preference: BuildingOffice2Icon,
  authority_unclear: QuestionMarkCircleIcon,
};

// Label mapping for risk signal types
const RISK_SIGNAL_LABELS: Record<RiskSignalType, string> = {
  budget_uncertainty: 'Budget Uncertainty',
  timeline_stall: 'Timeline Stall',
  competitor_preference: 'Competitor Preference',
  authority_unclear: 'Authority Unclear',
};

// Urgency border color mapping
const URGENCY_BORDER_COLORS: Record<GateUrgency, string> = {
  high: 'border-l-red-500',
  medium: 'border-l-yellow-500',
  low: 'border-l-purple-500',
};


export interface FlashcardProps {
  category: 'talk-about' | 'ask-about' | 'answer' | 'negotiate' | 'close' | 'skip';
  content: string;
  /**
   * Confidence score from LLM (0-100).
   * Currently unused in UI - visual confidence styling removed per Task 14.4.
   * Will be used for sorting/prioritization when LLM integration is added (Story 2.4).
   * @deprecated Visual display removed - kept for API compatibility
   */
  confidence?: number;
  isLoading?: boolean;
  /**
   * Story 2.6: Urgency level from LLM Gate decision
   * Affects border color and animation
   */
  urgency?: GateUrgency;
  /**
   * Story 2.6: Risk signals detected during coaching
   * Displayed as compact inline items below content
   */
  riskSignals?: RiskSignal[];
  /**
   * Story 2.4: Show tone indicator with stress level and trigger reasons
   */
  showToneIndicator?: boolean;
  /** Stress score for tone indicator (0-1). Required when showToneIndicator is true. */
  toneStressScore?: number;
  /** Trigger reasons for tone indicator. Required when showToneIndicator is true. */
  toneTriggerReasons?: string[];
  /**
   * Optional content rendered in the header row (right side).
   * Used for navigation arrows + freshness timestamp.
   */
  headerRight?: React.ReactNode;
  /**
   * Suggestion mode the content was generated under. Drives the header label
   * (e.g. teleprompter `ANSWER` vs topic `ANSWER GUIDE`) so the chip matches
   * what's being shown. Defaults to teleprompter if omitted.
   */
  mode?: 'teleprompter' | 'topic';
  /** Per-suggestion client tone, displayed as a chip beside the category label. */
  clientTone?: ClientTone | null;
}

/**
 * Flashcard Component - Story 1.6 Task 4 (Refactored) + Story 2.6
 *
 * Single persistent flashcard with glassmorphic styling and loading states
 * - Category-coded border (purple for talk-about, cyan for ask-about)
 * - Urgency-based border color override (high=red, medium=yellow)
 * - Loading state with animated dots
 * - Risk signals section with icons
 * - Cannot be dismissed - persistent display
 * - ARIA attributes for accessibility
 *
 * Story 2.6 Integration:
 * - Urgency from LLM Gate affects border color
 * - Risk signals displayed below content with icons
 */
export function Flashcard({
  category,
  content,
  isLoading = false,
  urgency,
  riskSignals,
  showToneIndicator = false,
  toneStressScore = 0,
  toneTriggerReasons = [],
  headerRight,
  mode = 'teleprompter',
  clientTone,
}: FlashcardProps) {
  const markdownContent = content.replace(/\\n/g, '\n');
  // Category styling - purple for talk-about, emerald for answer, cyan for ask-about
  // Urgency overrides category color when specified and not 'low'
  const categoryColor =
    category === 'talk-about' ? 'border-l-purple-500' :
    category === 'answer' ? 'border-l-emerald-400' :
    category === 'negotiate' ? 'border-l-rose-400' :
    category === 'close' ? 'border-l-amber-400' :
    'border-l-cyan-400';
  const borderColor = urgency && urgency !== 'low'
    ? URGENCY_BORDER_COLORS[urgency]
    : categoryColor;

  const categoryLabel = mode === 'topic'
    ? (category === 'talk-about' ? 'TOPIC' :
       category === 'answer' ? 'ANSWER GUIDE' :
       category === 'negotiate' ? 'NEGOTIATE' :
       category === 'close' ? 'WRAP UP' :
       'QUESTION')
    : (category === 'talk-about' ? 'TALK ABOUT' :
       category === 'answer' ? 'ANSWER' :
       category === 'negotiate' ? 'NEGOTIATE' :
       category === 'close' ? 'CLOSE' :
       'ASK QUESTION');

  // Filter to show only risk signals (if any)
  const visibleRiskSignals = riskSignals?.filter((rs) => rs.evidence) ?? [];

  return (
    <div
      role="article"
      aria-label={`Flashcard: ${categoryLabel.toLowerCase()}${isLoading ? ' - Loading' : ` - ${content}`}`}
      aria-live="polite"
      aria-busy={isLoading}
      tabIndex={0}
      className="overlay-focus-ring rounded-2xl"
    >
      <div className={`border-l-4 ${borderColor} p-5`}>
        {/* Category Header */}
        <div className="flex flex-wrap items-center justify-between mb-3 gap-x-2 gap-y-1">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className={`text-xs font-semibold uppercase tracking-wide shrink-0 ${
                isLoading ? 'text-white/50' : 'text-white/70'
              }`}
            >
              {categoryLabel}
            </div>
            {!isLoading && <ToneChip tone={clientTone} size="xs" />}
          </div>
          <div className="flex items-center gap-2 min-w-0">
            {/* Story 2.4: Tone Indicator */}
            {showToneIndicator && !isLoading && <ToneIndicator compact stressScore={toneStressScore} triggerReasons={toneTriggerReasons} />}
            {/* Navigation + timestamp (injected by parent) */}
            {headerRight}
          </div>
        </div>

        {/* Flashcard Content or Loading State */}
        {isLoading ? (
          <div className="text-base leading-relaxed text-white/50 animate-pulse">
            <div className="flex items-center space-x-1">
              <span className="animate-bounce" style={{ animationDelay: '0ms' }}>
                .
              </span>
              <span className="animate-bounce" style={{ animationDelay: '150ms' }}>
                .
              </span>
              <span className="animate-bounce" style={{ animationDelay: '300ms' }}>
                .
              </span>
            </div>
            <p className="text-sm mt-2">Listening for conversation context...</p>
          </div>
        ) : (
          <>
            <div className="text-base leading-relaxed text-white/90 break-words [&_p]:!text-base [&_ul]:!text-base [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:!text-base [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1">
              <ReactMarkdown>{markdownContent}</ReactMarkdown>
            </div>

            {/* Story 2.6: Risk Signals Section */}
            {visibleRiskSignals.length > 0 && (
              <div className="mt-4 pt-3 border-t border-white/10">
                <div className="space-y-2">
                  {visibleRiskSignals.map((risk, index) => {
                    const Icon = RISK_SIGNAL_ICONS[risk.type];
                    const label = RISK_SIGNAL_LABELS[risk.type];
                    return (
                      <div
                        key={index}
                        className="flex items-start gap-2 text-sm"
                      >
                        <Icon className="w-4 h-4 mt-0.5 text-amber-400 flex-shrink-0" />
                        <div>
                          <span className="text-amber-400 font-medium">{label}:</span>{' '}
                          <span className="text-white/70">{risk.evidence}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
